import React, { useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { ChartPoint } from "../../types";

interface MiniAreaChartProps {
  data: ChartPoint[];
  color: string;
  chartId: string;
}

const formatYAxisTick = (value: number) => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}k`;
  }
  return `${value}`;
};

const AXIS_TICK_STYLE = {
  fontSize: 12,
  fontWeight: 400,
  fill: "rgba(51, 51, 51, 1)",
  fontFamily: "Roboto, sans-serif",
};

const MiniAreaChart: React.FC<MiniAreaChartProps> = ({ data, color, chartId }) => {
  const gradientId = `ecosystem-gradient-${chartId}`;
  const yDomain = useMemo(() => {
    const values = data.map((point) => point.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = Math.max((max - min) * 0.2, 4);

    return [Math.max(0, min - padding), max + padding];
  }, [data]);

  return (
    <div className="mini-area-chart">
      <div className="mini-area-chart__y-label" aria-hidden>
        <span className="mini-area-chart__y-label-line">Monetary value</span>
        <span className="mini-area-chart__y-label-line">in USD</span>
      </div>
      <div className="mini-area-chart__plot">
        <ResponsiveContainer width="100%" height={112}>
          <AreaChart
            data={data}
            margin={{ top: 6, right: 6, left: 0, bottom: 2 }}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                <stop offset="100%" stopColor={color} stopOpacity={0.03} />
              </linearGradient>
            </defs>
            <CartesianGrid
              stroke="rgba(226, 232, 240, 1)"
              strokeDasharray="3 3"
              vertical
              horizontal
            />
            <XAxis
              dataKey="month"
              axisLine={{ stroke: "rgba(226, 232, 240, 1)" }}
              tickLine={false}
              tick={AXIS_TICK_STYLE}
              interval={0}
              dy={4}
            />
            <YAxis
              width={36}
              axisLine={{ stroke: "rgba(226, 232, 240, 1)" }}
              tickLine={false}
              tick={AXIS_TICK_STYLE}
              tickFormatter={formatYAxisTick}
              domain={yDomain}
              tickCount={4}
            />
            <Area
              type="natural"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={false}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <span className="mini-area-chart__x-label">Month</span>
    </div>
  );
};

export default MiniAreaChart;
